import PaymentInstructionLayout from "../layouts/PaymentInstructionLayout";
import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { CreditCard, Lock, AlertCircle, Eye, EyeOff, ChevronDown, ChevronUp } from "lucide-react";
import {
  formatCardNumber,
  formatExpiry,
  parseExpiryToYyMm,
  detectCardBrand,
  validateCardNumber,
  validateExpiry,
  validateCVV,
  isCardBrandSupported,
} from "../../paymentLinking/utils/cardUtils";
import { cardLogos } from "../../paymentLinking/utils/cardLogos";
import { authorizedFetch, SessionExpiredError } from "../../utils/authorizedFetch";
import { PaymentLinkErrorCode, getErrorMessage } from "../../utils/paymentLinkErrorHandler";
import ThreeDSChallengeModal from "./ThreeDSChallengeModal";
import useTranslation from "../hooks/useTranslation";
import { pickThreeDSChallengeContent } from "../utils/threeDSChallengeContent";

const BASE_URL = process.env.REACT_APP_API_URL;

/** Map detectCardBrand (lowercase) to cardLogos keys (Visa, Mastercard, JCB, AMEX) */
const CARD_BRAND_TO_LOGO_KEY = {
  visa: "Visa",
  mastercard: "Mastercard",
  jcb: "JCB",
  amex: "AMEX",
};

/** Fallback for unknown brand (e.g. card preview) */
const CardBrandLogoUnknown = () => (
  <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
    <CreditCard className="w-5 h-5 text-gray-400" />
  </div>
);

export default function PaymentInstructionCard({
  amount,
  orderId,
  merchantName,
  expired_at,
  selected_method,
  onCheckStatus,
  bankName,
  bankCode,
  historyId,
  /** Frictionless / post-3DS submit already done on method selection — poll webhook only. */
  initialAwaitingWebhook = false,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const customer = useSelector((state) => state.customer);
  const paymentLinkToken = useSelector((state) => state.payment.token);
  const [formData, setFormData] = useState({
    cardNumber: "",
    cardExpiry: "",
    cardCvv: "",
    cardHolderName: customer?.fullName?.toUpperCase() || "",
    cardEmail: customer?.email || "",
    address: customer?.address || "",
    city: customer?.city || "",
    state: customer?.state || "",
    postalCode: customer?.postalCode || "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [cardBrand, setCardBrand] = useState("unknown");
  const [showCvv, setShowCvv] = useState(false);
  const [showBillingDetails, setShowBillingDetails] = useState(
    () => Boolean(customer?.email || customer?.address || customer?.city || customer?.state || customer?.postalCode)
  );
  const [payLoading, setPayLoading] = useState(false);
  const [payError, setPayError] = useState(null);
  const [show3DSModal, setShow3DSModal] = useState(false);
  const [threeDSContent, setThreeDSContent] = useState(null);
  const [awaitingWebhook, setAwaitingWebhook] = useState(() => Boolean(initialAwaitingWebhook));
  /** Hide card form when payment was already submitted (method selection or after 3DS here). */
  const [cardSubmitDone, setCardSubmitDone] = useState(() => Boolean(initialAwaitingWebhook));
  const POLL_INTERVAL_MS = 3000;
  const POLL_TIMEOUT_MS = 120000;

  useEffect(() => {
    const brand = detectCardBrand(formData.cardNumber);
    setCardBrand(brand);
  }, [formData.cardNumber]);

  // Pre-fill cardholder name/email and billing address from Redux customer slice (set by FirstForm).
  // Only fills fields the user hasn't already touched/typed, so it won't clobber edits.
  useEffect(() => {
    setFormData((prev) => {
      const next = { ...prev };
      if (!prev.cardHolderName && customer?.fullName) {
        next.cardHolderName = customer.fullName.toUpperCase();
      }
      if (!prev.cardEmail && customer?.email) {
        next.cardEmail = customer.email;
      }
      if (!prev.address && customer?.address) {
        next.address = customer.address;
      }
      if (!prev.city && customer?.city) {
        next.city = customer.city;
      }
      if (!prev.state && customer?.state) {
        next.state = customer.state;
      }
      if (!prev.postalCode && customer?.postalCode) {
        next.postalCode = customer.postalCode;
      }
      return next;
    });
    if (customer?.email || customer?.address || customer?.city || customer?.state || customer?.postalCode) {
      setShowBillingDetails(true);
    }
  }, [customer?.fullName, customer?.email, customer?.address, customer?.city, customer?.state, customer?.postalCode]);

  const handleThreeDSResult = useCallback(
    (data) => {
      setShow3DSModal(false);
      setThreeDSContent(null);
      if (data?.success) {
        setPayError(null);
        setCardSubmitDone(true);
        setAwaitingWebhook(true);
      } else {
        setAwaitingWebhook(false);
        setCardSubmitDone(false);
        setPayError(data?.resultMessage || t("paymentInstructionCard.error3dsFailed"));
      }
    },
    [t],
  );

  // After 3DS auth succeeds, poll check-status until paid / failed / expired / timeout
  useEffect(() => {
    if (!awaitingWebhook || !historyId || !orderId) return;

    let cancelled = false;
    const startedAt = Date.now();

    const poll = async () => {
      if (cancelled) return;
      try {
        const response = await authorizedFetch(`${BASE_URL}/api/payment-link/check-status`, {
          dispatch,
          token: paymentLinkToken,
          refresh: { kind: "history", reffNo: orderId },
          method: "POST",
          body: JSON.stringify({
            history_id: historyId,
            reff_no: orderId,
          }),
        });
        const data = await response.json().catch(() => ({}));
        const status = data?.transaction?.status;

        if (status === "paid") {
          setAwaitingWebhook(false);
          onCheckStatus?.();
          return true;
        }
        if (status === "failed" || status === "expired" || status === "canceled") {
          setAwaitingWebhook(false);
          if (status === "failed") {
            setPayError(t("paymentInstructionCard.errorFailedAfterVerification"));
          }
          onCheckStatus?.();
          return true;
        }
      } catch (err) {
        console.error("Post-3DS status poll error:", err);
        if (err instanceof SessionExpiredError) {
          setAwaitingWebhook(false);
          setPayError(getErrorMessage(PaymentLinkErrorCode.SESSION_LOST));
          return true;
        }
      }

      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        setAwaitingWebhook(false);
        setPayError(t("paymentInstructionCard.errorStillConfirming"));
        return true;
      }
      return false;
    };

    let intervalId;
    (async () => {
      const done = await poll();
      if (!done && !cancelled) {
        intervalId = setInterval(async () => {
          const finished = await poll();
          if (finished && intervalId) clearInterval(intervalId);
        }, POLL_INTERVAL_MS);
      }
    })();

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [awaitingWebhook, historyId, orderId, paymentLinkToken, dispatch, onCheckStatus, t]);

  const applyOpenChallengeStatus = useCallback(
    (status) => {
      if (status === "paid") {
        setShow3DSModal(false);
        setThreeDSContent(null);
        setCardSubmitDone(true);
        setAwaitingWebhook(false);
        return true;
      }
      if (status === "expired") {
        setShow3DSModal(false);
        setThreeDSContent(null);
        return true;
      }
      if (status !== "failed") return false;
      setShow3DSModal(false);
      setThreeDSContent(null);
      setCardSubmitDone(false);
      setAwaitingWebhook(false);
      setPayError(t("paymentInstructionCard.error3dsFailed"));
      return true;
    },
    [t],
  );

  // Status polling while the 3DS modal is open.
  //
  // Nicepay's result page postMessages the parent (handled by ThreeDSChallengeModal),
  // but Midtrans's hosted 3DS finish page does not. Without a poll the iframe stalls
  // on Midtrans's "Transaction is pending. Tap to Continue." page even after the
  // backend webhook has settled the PaymentLinkHistory row.
  // Use silent check-status so pending ticks do not spam toasts; paid/expired still
  // navigate in ThirdForm. Failed closes the modal here because ThirdForm does not
  // leave the instruction page on failure.
  useEffect(() => {
    if (!show3DSModal || !onCheckStatus) return undefined;

    let cancelled = false;
    const tick = async () => {
      try {
        const status = await onCheckStatus({ silent: true });
        if (cancelled) return;
        applyOpenChallengeStatus(status);
      } catch (_) {}
    };

    tick();
    const id = setInterval(tick, 4000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [show3DSModal, onCheckStatus, applyOpenChallengeStatus]);

  const handleChange = (field, value) => {
    let formattedValue = value;
    if (field === "cardNumber") {
      formattedValue = formatCardNumber(value);
    } else if (field === "cardExpiry") {
      formattedValue = formatExpiry(value);
    } else if (field === "cardCvv") {
      formattedValue = value.replace(/\D/g, "").slice(0, cardBrand === "amex" ? 4 : 3);
    } else if (field === "cardHolderName") {
      formattedValue = value.toUpperCase();
    }
    setFormData((prev) => ({ ...prev, [field]: formattedValue }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field);
  };

  const validateField = (field) => {
    const newErrors = { ...errors };
    switch (field) {
      case "cardNumber": {
        const num = formData.cardNumber;
        const brand = detectCardBrand(num);
        if (!num || !num.replace(/\s/g, "")) {
          delete newErrors.cardNumber;
        } else if (!isCardBrandSupported(brand)) {
          newErrors.cardNumber = t("paymentInstructionCard.errorCardNotSupported");
        } else if (!validateCardNumber(num)) {
          newErrors.cardNumber = t("paymentInstructionCard.errorInvalidCardNumber");
        } else {
          delete newErrors.cardNumber;
        }
        break;
      }
      case "cardExpiry":
        if (formData.cardExpiry && !validateExpiry(formData.cardExpiry)) {
          newErrors.cardExpiry = t("paymentInstructionCard.errorInvalidExpiry");
        } else {
          delete newErrors.cardExpiry;
        }
        break;
      case "cardCvv":
        if (formData.cardCvv && !validateCVV(formData.cardCvv, cardBrand)) {
          newErrors.cardCvv = t(
            cardBrand === "amex" ? "paymentInstructionCard.errorCvvDigits4" : "paymentInstructionCard.errorCvvDigits3",
          );
        } else {
          delete newErrors.cardCvv;
        }
        break;
      case "cardHolderName":
        if (formData.cardHolderName && formData.cardHolderName.length < 2) {
          newErrors.cardHolderName = t("paymentInstructionCard.errorNameTooShort");
        } else {
          delete newErrors.cardHolderName;
        }
        break;
      case "cardEmail":
        if (formData.cardEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.cardEmail)) {
          newErrors.cardEmail = t("paymentInstructionCard.errorInvalidEmail");
        } else {
          delete newErrors.cardEmail;
        }
        break;
      case "address":
        if (formData.address && !formData.address.trim()) {
          newErrors.address = t("paymentInstructionCard.errorRequired");
        } else {
          delete newErrors.address;
        }
        break;
      case "city":
        if (formData.city && !formData.city.trim()) {
          newErrors.city = t("paymentInstructionCard.errorRequired");
        } else {
          delete newErrors.city;
        }
        break;
      case "state":
        if (formData.state && !formData.state.trim()) {
          newErrors.state = t("paymentInstructionCard.errorRequired");
        } else {
          delete newErrors.state;
        }
        break;
      case "postalCode":
        if (!formData.postalCode || !formData.postalCode.trim()) {
          delete newErrors.postalCode;
        } else if (!/^[\d\s\-a-zA-Z]{1,10}$/.test(formData.postalCode.trim())) {
          newErrors.postalCode = t("paymentInstructionCard.errorInvalidPostalCode");
        } else {
          delete newErrors.postalCode;
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const hasError = (field) => errors[field] && touched[field];

  const inputBaseClass = (field) =>
    `w-full h-12 px-4 border-2 rounded-lg text-base transition-all duration-200 outline-none disabled:bg-gray-100 disabled:cursor-not-allowed ${
      hasError(field)
        ? "border-red-400 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-200"
        : "border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-100"
    }`;

  const validateAll = useCallback(() => {
    const fields = ["cardNumber", "cardExpiry", "cardCvv", "cardHolderName"];
    if (showBillingDetails) {
      fields.push("cardEmail", "address", "city", "state", "postalCode");
    }
    const newErrors = {};
    const detectedBrand = detectCardBrand(formData.cardNumber);
    if (!formData.cardNumber || !formData.cardNumber.replace(/\s/g, "")) {
      newErrors.cardNumber = t("paymentInstructionCard.errorInvalidCardNumber");
    } else if (!isCardBrandSupported(detectedBrand)) {
      newErrors.cardNumber = t("paymentInstructionCard.errorCardNotSupported");
    } else if (!validateCardNumber(formData.cardNumber)) {
      newErrors.cardNumber = t("paymentInstructionCard.errorInvalidCardNumber");
    }
    if (!formData.cardExpiry || !validateExpiry(formData.cardExpiry)) {
      newErrors.cardExpiry = t("paymentInstructionCard.errorInvalidExpiry");
    }
    if (!formData.cardCvv || !validateCVV(formData.cardCvv, cardBrand)) {
      newErrors.cardCvv = t(
        cardBrand === "amex" ? "paymentInstructionCard.errorCvvDigits4" : "paymentInstructionCard.errorCvvDigits3",
      );
    }
    if (!formData.cardHolderName || formData.cardHolderName.trim().length < 2) {
      newErrors.cardHolderName = t("paymentInstructionCard.errorNameTooShort");
    }
    if (showBillingDetails) {
      if (formData.cardEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.cardEmail)) {
        newErrors.cardEmail = t("paymentInstructionCard.errorInvalidEmail");
      }
      if (formData.address && !formData.address.trim()) {
        newErrors.address = t("paymentInstructionCard.errorRequired");
      }
      if (formData.city && !formData.city.trim()) {
        newErrors.city = t("paymentInstructionCard.errorRequired");
      }
      if (formData.state && !formData.state.trim()) {
        newErrors.state = t("paymentInstructionCard.errorRequired");
      }
      if (formData.postalCode) {
        if (!formData.postalCode.trim()) {
          newErrors.postalCode = t("paymentInstructionCard.errorRequired");
        } else if (!/^[\d\s\-a-zA-Z]{1,10}$/.test(formData.postalCode.trim())) {
          newErrors.postalCode = t("paymentInstructionCard.errorInvalidPostalCode");
        }
      }
    }
    setErrors(newErrors);
    setTouched((prev) => {
      const next = { ...prev };
      fields.forEach((f) => { next[f] = true; });
      return next;
    });
    return Object.keys(newErrors).length === 0;
  }, [formData, cardBrand, showBillingDetails, t]);

  const handlePay = useCallback(async (e) => {
    e?.preventDefault();
    setPayError(null);
    if (!validateAll()) return;
    setPayLoading(true);
    try {
      const cardExpiryYyMm = parseExpiryToYyMm(formData.cardExpiry); // UI is MM/YY, send YYMM to API
      const res = await authorizedFetch(`${BASE_URL}/api/payment-link/card-payment`, {
        dispatch,
        token: paymentLinkToken,
        refresh: { kind: "history", reffNo: orderId },
        method: "POST",
        body: JSON.stringify({
          reff_no: orderId,
          card_number: formData.cardNumber.replace(/\s/g, ""),
          card_expiry: cardExpiryYyMm,
          card_cvv: formData.cardCvv,
          card_holder_name: formData.cardHolderName.trim(),
          ...(formData.cardEmail.trim() && { card_holder_email: formData.cardEmail.trim() }),
          ...(formData.address.trim() && { customer_address: formData.address.trim() }),
          ...(formData.city.trim() && { customer_city: formData.city.trim() }),
          ...(formData.state.trim() && { customer_state: formData.state.trim() }),
          ...(formData.postalCode.trim() && { customer_postal_code: formData.postalCode.trim() }),
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPayError(data.message || t("paymentInstructionCard.errorPaymentFailed"));
        return;
      }
      if (data.requires_3ds) {
        const challengeContent = pickThreeDSChallengeContent(data.data?.html_form, data.data?.payment_url);
        if (!challengeContent) {
          setPayError(t("paymentInstructionCard.error3dsStartFailed"));
          return;
        }
        setThreeDSContent(challengeContent);
        setShow3DSModal(true);
        return;
      }
      // Frictionless success: stay pending until Nicepay webhook confirms
      if (data.awaiting_webhook || data.data?.status === "pending") {
        setCardSubmitDone(true);
        setAwaitingWebhook(true);
        return;
      }
      onCheckStatus?.();
    } catch (err) {
      console.error("Card payment error:", err);
      setPayError(
        err instanceof SessionExpiredError
          ? getErrorMessage(PaymentLinkErrorCode.SESSION_LOST)
          : err.message || t("paymentInstructionCard.errorPaymentRequestFailed"),
      );
    } finally {
      setPayLoading(false);
    }
  }, [orderId, formData, validateAll, onCheckStatus, paymentLinkToken, dispatch, t]);

  const cardInstructions = t("paymentInstructionCard.instructions");

  return (
    <>
    <PaymentInstructionLayout
      amount={amount}
      orderId={orderId}
      merchantName={merchantName}
      expiredAt={expired_at}
      selectedMethod={selected_method}
      onCheckStatus={onCheckStatus}
      paymentCredential={
        <div className="mt-4 mb-6">
          {bankName && (
            <div className="flex items-center gap-2 justify-center mb-4">
              <CreditCard className="h-10 w-10 text-gray-700" aria-hidden />
              <h3 className="text-xl font-bold">{bankName}</h3>
            </div>
          )}
          <p className="text-gray-600 text-center mb-4">
            {cardSubmitDone
              ? t("paymentInstructionCard.challengeHandoffIntro")
              : t("paymentInstructionCard.intro")}
          </p>

          {cardSubmitDone ? (
            <div className="max-w-md mx-auto mb-6 rounded-xl border border-blue-200 bg-blue-50 p-5 text-center text-blue-900">
              <Lock className="mx-auto mb-3 h-8 w-8 text-blue-600" aria-hidden />
              <p className="font-semibold">
                {show3DSModal
                  ? t("paymentInstructionCard.completeBankVerification")
                  : awaitingWebhook
                    ? t("paymentInstructionCard.confirmingPayment")
                    : t("paymentInstructionCard.processingCardPayment")}
              </p>
              <p className="mt-2 text-sm text-blue-800">
                {t("paymentInstructionCard.noNeedReenterCard")}
              </p>
            </div>
          ) : (
          <>
          {/* Card preview (same style as CreditCardForm) */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-5 text-white shadow-lg mb-6">
            <div className="flex justify-between items-start mb-8">
              <div className="text-xs uppercase tracking-wider text-gray-400">
                {t("paymentInstructionCard.cardDetails")}
              </div>
              {CARD_BRAND_TO_LOGO_KEY[cardBrand] && cardLogos[CARD_BRAND_TO_LOGO_KEY[cardBrand]] ? (
                <img
                  src={cardLogos[CARD_BRAND_TO_LOGO_KEY[cardBrand]]}
                  alt={cardBrand}
                  className="w-10 h-7 object-contain"
                />
              ) : (
                <CardBrandLogoUnknown />
              )}
            </div>
            <div className="font-mono text-xl tracking-widest mb-6">
              {formData.cardNumber || "•••• •••• •••• ••••"}
            </div>
            <div className="flex justify-between items-end">
              <div>
                <div className="text-[10px] uppercase text-gray-400 mb-1">
                  {t("paymentInstructionCard.cardHolder")}
                </div>
                <div className="text-sm font-medium tracking-wide">
                  {formData.cardHolderName || t("paymentInstructionCard.cardHolderPlaceholder")}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase text-gray-400 mb-1">
                  {t("paymentInstructionCard.expires")}
                </div>
                <div className="text-sm font-medium">{formData.cardExpiry || "MM/YY"}</div>
              </div>
            </div>
          </div>

          {/* Card input fields (same layout/styling as CreditCardForm) */}
          <form className="space-y-4 max-w-md mx-auto">
            <div>
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <label className="text-sm font-medium text-gray-700">
                  {t("paymentInstructionCard.cardNumberLabel")} <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2 shrink-0">
                  <img src={cardLogos.Visa} alt="Visa" className="w-8 h-5 object-contain" />
                  <img src={cardLogos.Mastercard} alt="Mastercard" className="w-8 h-5 object-contain" />
                  <img src={cardLogos.JCB} alt="JCB" className="w-8 h-5 object-contain" />
                  <img src={cardLogos.AMEX} alt="American Express" className="w-8 h-5 object-contain" />
                </div>
              </div>
              <div className="relative">
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-number"
                  value={formData.cardNumber}
                  onChange={(e) => handleChange("cardNumber", e.target.value)}
                  onBlur={() => handleBlur("cardNumber")}
                  placeholder="1234 5678 9012 3456"
                  maxLength={19}
                  className={`${inputBaseClass("cardNumber")} pr-14 font-mono`}
                />
                {CARD_BRAND_TO_LOGO_KEY[cardBrand] && cardLogos[CARD_BRAND_TO_LOGO_KEY[cardBrand]] && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-5 flex items-center justify-center">
                    <img
                      src={cardLogos[CARD_BRAND_TO_LOGO_KEY[cardBrand]]}
                      alt={cardBrand}
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
              </div>
              {hasError("cardNumber") && (
                <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {errors.cardNumber}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t("paymentInstructionCard.expiryLabel")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-exp"
                  value={formData.cardExpiry}
                  onChange={(e) => handleChange("cardExpiry", e.target.value)}
                  onBlur={() => handleBlur("cardExpiry")}
                  placeholder="MM/YY"
                  maxLength={5}
                  className={`${inputBaseClass("cardExpiry")} font-mono text-center`}
                />
                {hasError("cardExpiry") && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {errors.cardExpiry}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t("paymentInstructionCard.cvvLabel")} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showCvv ? "text" : "password"}
                    inputMode="numeric"
                    autoComplete="cc-csc"
                    value={formData.cardCvv}
                    onChange={(e) => handleChange("cardCvv", e.target.value)}
                    onBlur={() => handleBlur("cardCvv")}
                    placeholder="•••"
                    maxLength={cardBrand === "amex" ? 4 : 3}
                    className={`${inputBaseClass("cardCvv")} pr-10 font-mono text-center`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCvv(!showCvv)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showCvv ? t("paymentInstructionCard.hideCvv") : t("paymentInstructionCard.showCvv")}
                  >
                    {showCvv ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {hasError("cardCvv") && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {errors.cardCvv}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t("paymentInstructionCard.cardholderNameLabel")} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                autoComplete="cc-name"
                value={formData.cardHolderName}
                onChange={(e) => handleChange("cardHolderName", e.target.value)}
                onBlur={() => handleBlur("cardHolderName")}
                placeholder="JOHN DOE"
                className={`${inputBaseClass("cardHolderName")} uppercase tracking-wide`}
              />
              {hasError("cardHolderName") && (
                <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {errors.cardHolderName}
                </p>
              )}
            </div>

            <div className="border-t border-gray-200 pt-4">
              <button
                type="button"
                onClick={() => setShowBillingDetails((prev) => !prev)}
                className="w-full flex justify-between items-center p-3 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                aria-expanded={showBillingDetails}
              >
                <span>
                  {t("paymentInstructionCard.emailAndBillingAddress")}{" "}
                  <span className="text-gray-400 font-normal">{t("paymentInstructionCard.optional")}</span>
                </span>
                {showBillingDetails ? (
                  <ChevronUp className="w-5 h-5 shrink-0 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 shrink-0 text-gray-500" />
                )}
              </button>
              {showBillingDetails && (
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      {t("paymentInstructionCard.emailLabel")}
                    </label>
                    <input
                      type="email"
                      autoComplete="email"
                      value={formData.cardEmail}
                      onChange={(e) => handleChange("cardEmail", e.target.value)}
                      onBlur={() => handleBlur("cardEmail")}
                      placeholder="email@example.com"
                      className={inputBaseClass("cardEmail")}
                    />
                    <p className="text-xs text-gray-500 mt-1">{t("paymentInstructionCard.emailHelper")}</p>
                    {hasError("cardEmail") && (
                      <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {errors.cardEmail}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      {t("paymentInstructionCard.billingAddressLabel")}
                    </label>
                    <input
                      type="text"
                      autoComplete="street-address"
                      value={formData.address}
                      onChange={(e) => handleChange("address", e.target.value)}
                      onBlur={() => handleBlur("address")}
                      placeholder={t("paymentInstructionCard.placeholderStreetAddress")}
                      className={inputBaseClass("address")}
                    />
                    {hasError("address") && (
                      <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {errors.address}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {t("paymentInstructionCard.cityLabel")}
                      </label>
                      <input
                        type="text"
                        autoComplete="address-level2"
                        value={formData.city}
                        onChange={(e) => handleChange("city", e.target.value)}
                        onBlur={() => handleBlur("city")}
                        placeholder={t("paymentInstructionCard.placeholderCity")}
                        className={inputBaseClass("city")}
                      />
                      {hasError("city") && (
                        <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5" />
                          {errors.city}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {t("paymentInstructionCard.stateLabel")}
                      </label>
                      <input
                        type="text"
                        autoComplete="address-level1"
                        value={formData.state}
                        onChange={(e) => handleChange("state", e.target.value)}
                        onBlur={() => handleBlur("state")}
                        placeholder={t("paymentInstructionCard.placeholderState")}
                        className={inputBaseClass("state")}
                      />
                      {hasError("state") && (
                        <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5" />
                          {errors.state}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      {t("paymentInstructionCard.postalCodeLabel")}
                    </label>
                    <input
                      type="text"
                      autoComplete="postal-code"
                      value={formData.postalCode}
                      onChange={(e) => handleChange("postalCode", e.target.value)}
                      onBlur={() => handleBlur("postalCode")}
                      placeholder={t("paymentInstructionCard.placeholderPostalCode")}
                      maxLength={10}
                      className={inputBaseClass("postalCode")}
                    />
                    {hasError("postalCode") && (
                      <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {errors.postalCode}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </form>

          <div className="flex items-center gap-2 text-xs text-gray-500 mt-4 max-w-md mx-auto">
            <Lock className="w-4 h-4 text-green-600 shrink-0" />
            <span>{t("paymentInstructionCard.secureNotice")}</span>
          </div>
          </>
          )}

          {awaitingWebhook && !cardSubmitDone && (
            <div className="mt-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-sm max-w-md mx-auto">
              {t("paymentInstructionCard.verificationCompletedConfirming")}
            </div>
          )}

          {payError && (
            <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2 max-w-md mx-auto">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {payError}
            </div>
          )}

          {!cardSubmitDone && (
          <div className="mt-6 max-w-md mx-auto">
            <button
              type="button"
              onClick={handlePay}
              disabled={payLoading || awaitingWebhook}
              className="w-full py-4 px-6 rounded-lg text-lg font-semibold bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white transition-colors"
            >
              {awaitingWebhook
                ? t("paymentInstructionCard.confirmingPayment")
                : payLoading
                ? t("common.processing")
                : t("paymentInstructionCard.payNow")}
            </button>
          </div>
          )}

          {cardSubmitDone && awaitingWebhook && (
            <div className="mt-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-sm max-w-md mx-auto text-center">
              {t("paymentInstructionCard.verificationCompletedConfirming")}
            </div>
          )}

          {!cardSubmitDone && show3DSModal === false && !awaitingWebhook && !payError && threeDSContent && (
            <div className="mt-4 max-w-md mx-auto">
              <button
                type="button"
                onClick={() => setShow3DSModal(true)}
                className="w-full py-4 px-6 rounded-lg text-lg font-semibold bg-green-600 hover:bg-green-700 text-white transition-colors"
              >
                {t("paymentInstructionCard.continueVerification")}
              </button>
            </div>
          )}
        </div>
      }
      howToPay={
        <div className="space-y-4 mt-6">
          <h3 className="text-xl font-bold">{t("paymentInstructionCard.howToPay")}</h3>
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <ol className="list-decimal list-inside space-y-2 text-gray-600">
              {cardInstructions.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          </div>
        </div>
      }
    />

    <ThreeDSChallengeModal
      isOpen={show3DSModal}
      content={threeDSContent}
      onClose={() => setShow3DSModal(false)}
      onResult={handleThreeDSResult}
      onIframeLoad={async () => {
        try {
          const status = await onCheckStatus?.({ silent: true });
          applyOpenChallengeStatus(status);
        } catch (_) {}
      }}
    />
    </>
  );
}
